import { LitElement, html, css } from 'lit';
import { readPrefsState, writePrefs } from '../../runtime/prefs.js';

// Variant → accent token
const VARIANT_TOKEN = {
  brand:   'var(--crusher-color-brand-primary)',
  info:    'var(--crusher-color-status-info)',
  success: 'var(--crusher-color-status-success)',
  warning: 'var(--crusher-color-status-warning)',
  danger:  'var(--crusher-color-status-failure)',
};

const VALID_VARIANTS = new Set(Object.keys(VARIANT_TOKEN));

// Human-readable label for aria-label on the region.
const VARIANT_LABEL = {
  brand:   'Brand announcement',
  info:    'Info announcement',
  success: 'Success announcement',
  warning: 'Warning announcement',
  danger:  'Danger announcement',
};

export class CrusherBanner extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    :host([hidden]) {
      display: none;
    }

    .banner {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-3);
      padding: var(--crusher-spacing-3) var(--crusher-spacing-6);
      background: color-mix(in srgb, var(--banner-accent) 8%, transparent);
      border-block-end: 1px solid var(--banner-accent);
      width: 100%;
    }

    /* Variant accent token bindings */
    .banner--brand   { --banner-accent: var(--crusher-brand-on-surface, var(--crusher-color-brand-primary)); }
    .banner--info    { --banner-accent: var(--crusher-color-status-info); }
    .banner--success { --banner-accent: var(--crusher-color-status-success); }
    .banner--warning { --banner-accent: var(--crusher-color-status-warning); }
    .banner--danger  { --banner-accent: var(--crusher-color-status-failure); }

    .banner__content {
      flex: 1;
      text-align: center;
      font-size: var(--crusher-font-size-sm);
      color: var(--crusher-text-primary);
      line-height: var(--crusher-line-height-relaxed, 1.6);
    }

    ::slotted(a) {
      color: var(--banner-accent);
      font-weight: var(--crusher-font-weight-semibold);
    }

    .banner__actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--crusher-spacing-2);
      flex-shrink: 0;
    }

    .banner__close {
      appearance: none;
      background: transparent;
      border: none;
      padding: 0;
      margin: 0;
      margin-inline-start: auto;
      cursor: var(--crusher-banner-cursor, pointer);
      color: var(--crusher-text-secondary);
      font-size: var(--crusher-font-size-base);
      line-height: 1;
      border-radius: var(--crusher-radius-sm);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: calc(var(--crusher-spacing-4) + var(--crusher-spacing-2));
      height: calc(var(--crusher-spacing-4) + var(--crusher-spacing-2));
      transition:
        color var(--crusher-motion-duration-fast, 150ms) ease,
        background-color var(--crusher-motion-duration-fast, 150ms) ease;
    }

    .banner__close:hover {
      color: var(--crusher-text-primary);
      background: color-mix(in srgb, var(--crusher-text-primary) 10%, transparent);
    }

    .banner__close:focus-visible {
      outline: var(--crusher-focus-width, 2px) solid var(--crusher-color-brand-primary);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .banner__close {
        transition: none;
      }
    }
  `;

  static properties = {
    variant:      { type: String, reflect: true },
    dismissible:  { type: Boolean, reflect: true },
    _isDismissed: { type: Boolean, state: true },
  };

  constructor() {
    super();
    this.variant = 'brand';
    this.dismissible = false;
    this._isDismissed = false;
  }

  // ---- Lifecycle ----

  connectedCallback() {
    super.connectedCallback();
    if (this.dismissible && this.id) {
      const { prefs } = readPrefsState();
      if (prefs.bannersDismissed && prefs.bannersDismissed[this.id] === true) {
        this._isDismissed = true;
        this.hidden = true;
      }
    }
  }

  // ---- Dismiss handler ----

  _dismiss() {
    if (this.id) {
      writePrefs((prefs) => {
        const dismissed = prefs.bannersDismissed || {};
        dismissed[this.id] = true;
        return { ...prefs, bannersDismissed: dismissed };
      });
    }
    this._isDismissed = true;
    this.hidden = true;
    this.dispatchEvent(new CustomEvent('crusher:bannerdismiss', {
      bubbles: true,
      composed: true,
      detail: { id: this.id || null },
    }));
  }

  // ---- Render ----

  render() {
    if (this._isDismissed) return html``;

    const v = VALID_VARIANTS.has(this.variant) ? this.variant : 'brand';
    const label = VARIANT_LABEL[v];

    return html`
      <div class="banner banner--${v}" role="region" aria-label=${label}>
        <div class="banner__content">
          <slot></slot>
        </div>
        ${this.dismissible ? html`
          <div class="banner__actions"><slot name="actions"></slot></div>
        ` : ''}
        ${this.dismissible ? html`
          <button
            type="button"
            class="banner__close"
            aria-label="Dismiss banner"
            @click=${this._dismiss}
          >&#215;</button>
        ` : ''}
      </div>
    `;
  }
}

CrusherBanner._schemaWarned = false;

customElements.define('crusher-banner', CrusherBanner);
